"use client";

import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import axiosInstance from "@/utils/axiosInstance";
import { API_ENDPOINTS } from "@/constants/config";

const getInitialFormData = (teacher) => {
  if (!teacher) {
    return {
      profileImage: null,
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      teacherCode: "",
      aadhaarCard: "",
      phoneNumber: "",
      subjects: [],
      birthday: "",
      qualification: "",
      bloodGroup: "",
      gender: "",
      address: { street: "", city: "", state: "", pincode: "", country: "India" },
      motherName: "",
      fatherName: "",
      dateOfJoining: "",
      maritalStatus: "",
      panNumber: "",
      epf: "",
      basicSalary: "",
      allowance: "",
      teacherType: "",
      contractType: "",
      status: "active",
      bankAccountDetails: { accountHolderName: "", accountNumber: "", bankName: "", ifsc: "" },
    };
  }
  let subjectIds = [];
  if (Array.isArray(teacher.subjects) && teacher.subjects.length > 0) {
    subjectIds = typeof teacher.subjects[0] === "object" && teacher.subjects[0]._id
      ? teacher.subjects.map((s) => s._id)
      : teacher.subjects;
  } else if (teacher.subject?._id) subjectIds = [teacher.subject._id];
  else if (teacher.subject) subjectIds = [teacher.subject];
  const birthDate = teacher.dateOfBirth || teacher.birthday;
  const formattedDate = birthDate ? new Date(birthDate).toISOString().split("T")[0] : "";
  const dateOfJoining = teacher.dateOfJoining ? new Date(teacher.dateOfJoining).toISOString().split("T")[0] : "";
  return {
    profileImage: teacher.profileImage ?? null,
    firstName: teacher.firstName || teacher.name?.split(" ")[0] || "",
    middleName: teacher.middleName || teacher.name?.split(" ")[1] || "",
    lastName: teacher.lastName || teacher.name?.split(" ")[2] || "",
    email: teacher.email || "",
    teacherCode: teacher.employeeCode || teacher.teacherCode || "",
    aadhaarCard: teacher.aadhaarCard || "",
    phoneNumber: teacher.phoneNumber || "",
    subjects: subjectIds,
    birthday: formattedDate,
    qualification: teacher.qualification || "",
    bloodGroup: teacher.bloodGroup || "",
    gender: teacher.gender || "",
    address: teacher.address || { street: "", city: "", state: "", pincode: "", country: "India" },
    motherName: teacher.motherName || "",
    fatherName: teacher.fatherName || "",
    dateOfJoining,
    maritalStatus: teacher.maritalStatus || "",
    panNumber: teacher.panNumber || "",
    epf: teacher.epf || "",
    basicSalary: teacher.basicSalary ?? "",
    allowance: teacher.allowance ?? "",
    teacherType: teacher.teacherType || "",
    contractType: teacher.contractType || "",
    status: teacher.status || "active",
    bankAccountDetails: teacher.bankAccountDetails && typeof teacher.bankAccountDetails === "object"
      ? {
          accountHolderName: teacher.bankAccountDetails.accountHolderName || "",
          accountNumber: teacher.bankAccountDetails.accountNumber || "",
          bankName: teacher.bankAccountDetails.bankName || "",
          ifsc: teacher.bankAccountDetails.ifsc || "",
        }
      : { accountHolderName: "", accountNumber: "", bankName: "", ifsc: "" },
  };
};

const EditProfileModal = ({
  isOpen,
  onClose,
  teacher,
  onUpdate,
  subjects,
}) => {
  const [formData, setFormData] = useState(() => getInitialFormData(teacher));
  const [fieldErrors, setFieldErrors] = useState({});

  // Real-time validation function
  const validateField = (name, value) => {
    const errors = { ...fieldErrors };
    
    switch (name) {
      case "firstName":
        if (!value || value.trim() === "") {
          errors.firstName = "First name is required";
        } else if (value.length < 2) {
          errors.firstName = "First name must be at least 2 characters long";
        } else {
          delete errors.firstName;
        }
        break;
        
      case "lastName":
        // Optional: allow empty, space, or placeholder (e.g. "-") for no last name
        delete errors.lastName;
        break;
        
      case "email":
        // Email is optional; if provided validate format
        if (value && value.trim() !== "") {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            errors.email = "Please enter a valid email address";
          } else {
            delete errors.email;
          }
        } else {
          delete errors.email;
        }
        break;
        
      case "phoneNumber":
        // Phone number is optional; if provided validate format
        if (value && value.trim() !== "") {
          const phoneRegex = /^[0-9]{10}$/;
          if (!phoneRegex.test(value.replace(/\s/g, ""))) {
            errors.phoneNumber = "Phone number must be exactly 10 digits";
          } else {
            delete errors.phoneNumber;
          }
        } else {
          delete errors.phoneNumber;
        }
        break;
        
      case "teacherCode":
        if (!value || value.trim() === "") {
          errors.teacherCode = "Teacher code is required";
        } else {
          delete errors.teacherCode;
        }
        break;
        
      case "qualification":
        // Qualification is optional
        delete errors.qualification;
        break;
        
      case "address.pincode":
        // Address optional; if pincode provided validate format
        if (value && value.trim() !== "") {
          const pincodeRegex = /^[0-9]{6}$/;
          if (!pincodeRegex.test(value)) {
            errors.pincode = "Pincode must be exactly 6 digits";
          } else {
            delete errors.pincode;
          }
        } else {
          delete errors.pincode;
        }
        break;
        
      case "birthday":
        // Optional; if provided validate age range
        if (value) {
          const birthDate = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - birthDate.getFullYear();
          if (age < 15 || age > 100) {
            errors.birthday = "Teacher age must be between 15 and 100 years";
          } else {
            delete errors.birthday;
          }
        } else {
          delete errors.birthday;
        }
        break;
        
      case "bloodGroup":
        // Blood group is optional
        delete errors.bloodGroup;
        break;
        
      default:
        break;
    }
    
    setFieldErrors(errors);
  };

  useEffect(() => {
    if (isOpen && teacher) {
      setFormData(getInitialFormData(teacher));
    }
  }, [isOpen, teacher]);

  // Clear field errors when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setFieldErrors({});
    }
  }, [isOpen]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    validateField(e.target.name, e.target.value);
  };

  const handleSubjectToggle = (subjectId) => {
    setFormData((prev) => {
      const currentSubjects = prev.subjects;
      const isSelected = currentSubjects.includes(subjectId);

      return {
        ...prev,
        subjects: isSelected
          ? currentSubjects.filter((id) => id !== subjectId)
          : [...currentSubjects, subjectId],
      };
    });
  };

  const handleBankDetailChange = (e) => {
    const { name, value } = e.target;
    const key = name.replace("bankAccountDetails.", "");
    setFormData((prev) => ({
      ...prev,
      bankAccountDetails: {
        ...prev.bankAccountDetails,
        [key]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Enhanced frontend validation with specific error messages
    const validationErrors = [];

    // Basic required field validation
    if (!formData.firstName || formData.firstName.trim() === "") {
      validationErrors.push("First name is required");
    } else if (formData.firstName.length < 2) {
      validationErrors.push("First name must be at least 2 characters long");
    }

    // Last name optional (allow empty, space, or placeholder e.g. "-")

    // Email is optional; if provided validate format
    if (formData.email && formData.email.trim() !== "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        validationErrors.push("Please enter a valid email address");
      }
    }

    // Phone number is optional; if provided validate format
    if (formData.phoneNumber && formData.phoneNumber.trim() !== "") {
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(formData.phoneNumber.replace(/\s/g, ""))) {
        validationErrors.push("Phone number must be exactly 10 digits");
      }
    }

    if (!formData.teacherCode || formData.teacherCode.trim() === "") {
      validationErrors.push("Teacher code is required");
    }

    // Qualification is optional

    // Birthday optional; if provided validate age range
    if (formData.birthday) {
      const birthDate = new Date(formData.birthday);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 15 || age > 100) {
        validationErrors.push("Teacher age must be between 15 and 100 years");
      }
    }

    // Blood group is optional

    // Address optional; if pincode provided validate format
    if (formData.address?.pincode && formData.address.pincode.trim() !== "") {
      const pincodeRegex = /^[0-9]{6}$/;
      if (!pincodeRegex.test(formData.address.pincode)) {
        validationErrors.push("Pincode must be exactly 6 digits");
      }
    }

    // Show frontend validation errors
    if (validationErrors.length > 0) {
      toast.error(`Please fix the following errors:\n${validationErrors.join('\n')}`);
      return;
    }

    try {
      const formDataToSend = new FormData();

      // If image selected and it's not a string (already uploaded)
      if (formData.profileImage && typeof formData.profileImage !== "string") {
        formDataToSend.append("file", formData.profileImage);
      }

      // Copy formData, remove profileImage field
      const dataToSend = { ...formData };
      delete dataToSend.profileImage;
      
      // Map teacherCode to employeeCode for backend
      if (dataToSend.teacherCode) {
        dataToSend.employeeCode = dataToSend.teacherCode;
        delete dataToSend.teacherCode;
      }

      formDataToSend.append("data", JSON.stringify(dataToSend));

      const token = localStorage.getItem("token");
      await axiosInstance.put(
        API_ENDPOINTS.user(teacher._id),
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success("Teacher profile updated successfully");
      
      // Close modal and call onUpdate
      onClose();
      onUpdate?.(teacher._id, dataToSend);
      
      // Refresh the page automatically
      window.location.reload();
      
    } catch (error) {
      toast.error("Failed to update teacher profile");
      console.error("Error updating teacher:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex h-[100dvh] w-[100vw] max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 sm:h-auto sm:max-h-[90vh] sm:w-[calc(100vw-2rem)] sm:max-w-4xl sm:rounded-lg sm:border">
        <DialogHeader className="shrink-0 border-b px-4 py-4 sm:px-6">
          <DialogTitle>Edit Teacher Profile</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            <div className="space-y-4">
              <div className="mb-2 flex justify-center">
                <div className="relative w-28 sm:w-32">
                  <div className="group aspect-square w-full overflow-hidden rounded-full border-2 border-gray-200">
              {formData.profileImage ? (
                <img
                  src={
                    typeof formData.profileImage === "string"
                      ? formData.profileImage
                      : URL.createObjectURL(formData.profileImage)
                  }
                  alt="Profile Preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                  <svg
                    className="w-12 h-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
              )}
              <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setFormData((prev) => ({ ...prev, profileImage: file }));
                    }
                  }}
                  className="hidden"
                />
              </label>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:col-span-2 xl:col-span-1">
              <div>
                <label className="text-sm font-medium">First Name *</label>
                <Input
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                />
                {fieldErrors.firstName && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.firstName}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Middle Name</label>
                <Input
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Last Name (optional)</label>
                <Input
                  name="lastName"
                  placeholder="e.g. leave blank or use - if no last name"
                  value={formData.lastName}
                  onChange={handleChange}
                />
                {fieldErrors.lastName && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.lastName}</p>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
              {fieldErrors.email && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Teacher Code *</label>
              <Input
                name="teacherCode"
                value={formData.teacherCode}
                onChange={handleChange}
              />
              {fieldErrors.teacherCode && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.teacherCode}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Aadhaar Card</label>
              <Input
                name="aadhaarCard"
                value={formData.aadhaarCard}
                onChange={handleChange}
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Phone Number</label>
              <Input
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
              />
              {fieldErrors.phoneNumber && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.phoneNumber}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Status</label>
              <Select
                value={formData.status || "active"}
                onValueChange={(value) =>
                  handleChange({ target: { name: "status", value } })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Subjects</label>
              {formData.subjects.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1 mb-2 p-2 rounded-md bg-muted/50 border border-border/50">
                  {subjects
                    .filter((s) => formData.subjects.includes(s._id))
                    .map((subject) => (
                      <span
                        key={subject._id}
                        className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 dark:bg-green-950/40 dark:text-green-400 border border-green-200 dark:border-green-800 rounded-full px-2 py-0.5"
                      >
                        <span className="text-green-600 dark:text-green-500" aria-hidden>✓</span>
                        {subject.name}
                      </span>
                    ))}
                </div>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    {formData.subjects.length > 0
                      ? `${formData.subjects.length} subject(s) selected`
                      : "Select Subjects"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 max-h-60 overflow-y-auto">
                  <DropdownMenuLabel>Subjects</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {subjects.map((subject) => (
                    <DropdownMenuItem
                      key={subject._id}
                      onSelect={(e) => {
                        e.preventDefault(); // Prevent dropdown from closing
                      }}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={`subject-${subject._id}`}
                        checked={formData.subjects.includes(subject._id)}
                        onCheckedChange={() => handleSubjectToggle(subject._id)}
                      />
                      <label
                        htmlFor={`subject-${subject._id}`}
                        className="cursor-pointer"
                      >
                        {subject.name}
                      </label>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div>
              <label className="text-sm font-medium">Birthday (optional)</label>
              <Input
                name="birthday"
                type="date"
                value={formData.birthday || ""}
                onChange={handleChange}
                max={new Date().toISOString().split("T")[0]}
              />
              {fieldErrors.birthday && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.birthday}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Qualification</label>
              <Input
                name="qualification"
                value={formData.qualification}
                onChange={handleChange}
              />
              {fieldErrors.qualification && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.qualification}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Blood Group</label>
              <Select
                name="bloodGroup"
                value={formData.bloodGroup}
                onValueChange={(value) =>
                  handleChange({ target: { name: "bloodGroup", value } })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Blood Group" />
                </SelectTrigger>
                <SelectContent>
                  {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map(
                    (group) => (
                      <SelectItem key={group} value={group}>
                        {group}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
              {fieldErrors.bloodGroup && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.bloodGroup}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Gender</label>
              <Select
                name="gender"
                value={formData.gender}
                onValueChange={(value) =>
                  handleChange({ target: { name: "gender", value } })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Gender" />
                </SelectTrigger>
                <SelectContent>
                  {["Male", "Female", "Other"].map((gender) => (
                    <SelectItem key={gender} value={gender}>
                      {gender}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="border-t pt-4 lg:col-span-2">
              <p className="text-sm font-semibold text-gray-700 mb-3">Employment & Personal</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Mother&apos;s Name</label>
                  <Input name="motherName" value={formData.motherName} onChange={handleChange} />
                </div>
                <div>
                  <label className="text-sm font-medium">Father&apos;s Name</label>
                  <Input name="fatherName" value={formData.fatherName} onChange={handleChange} />
                </div>
                <div>
                  <label className="text-sm font-medium">Date of Joining</label>
                  <Input name="dateOfJoining" type="date" value={formData.dateOfJoining} onChange={handleChange} />
                </div>
                <div>
                  <label className="text-sm font-medium">Marital Status</label>
                  <Select
                    value={formData.maritalStatus}
                    onValueChange={(value) => handleChange({ target: { name: "maritalStatus", value } })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single">Single</SelectItem>
                      <SelectItem value="Married">Married</SelectItem>
                      <SelectItem value="Divorced">Divorced</SelectItem>
                      <SelectItem value="Widowed">Widowed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">PAN (optional)</label>
                  <Input name="panNumber" value={formData.panNumber} onChange={handleChange} />
                </div>
                <div>
                  <label className="text-sm font-medium">Teacher Type</label>
                  <Select
                    value={formData.teacherType}
                    onValueChange={(value) => handleChange({ target: { name: "teacherType", value } })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PRT">PRT</SelectItem>
                      <SelectItem value="TGT">TGT</SelectItem>
                      <SelectItem value="PGT">PGT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Contract Type</label>
                  <Input name="contractType" value={formData.contractType} onChange={handleChange} placeholder="e.g. Permanent" />
                </div>
              </div>
            </div>

            <div className="border-t pt-4 lg:col-span-2">
              <p className="text-sm font-semibold text-gray-700 mb-3">Salary Details</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">EPF</label>
                  <Input name="epf" value={formData.epf} onChange={handleChange} />
                </div>
                <div>
                  <label className="text-sm font-medium">Basic Salary</label>
                  <Input name="basicSalary" type="number" min={0} value={formData.basicSalary} onChange={handleChange} />
                </div>
                <div>
                  <label className="text-sm font-medium">Allowance</label>
                  <Input name="allowance" type="number" min={0} value={formData.allowance} onChange={handleChange} />
                </div>
              </div>
            </div>

            <div className="border-t pt-4 lg:col-span-2">
              <p className="text-sm font-semibold text-gray-700 mb-3">Bank Account Details</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Account Holder Name</label>
                  <Input name="bankAccountDetails.accountHolderName" value={formData.bankAccountDetails?.accountHolderName || ""} onChange={handleBankDetailChange} />
                </div>
                <div>
                  <label className="text-sm font-medium">Account Number</label>
                  <Input name="bankAccountDetails.accountNumber" value={formData.bankAccountDetails?.accountNumber || ""} onChange={handleBankDetailChange} />
                </div>
                <div>
                  <label className="text-sm font-medium">Bank Name</label>
                  <Input name="bankAccountDetails.bankName" value={formData.bankAccountDetails?.bankName || ""} onChange={handleBankDetailChange} />
                </div>
                <div>
                  <label className="text-sm font-medium">IFSC</label>
                  <Input name="bankAccountDetails.ifsc" value={formData.bankAccountDetails?.ifsc || ""} onChange={handleBankDetailChange} />
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <label className="text-sm font-medium">Address</label>
              <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Street Address</label>
                  <Input
                    name="address.street"
                    value={formData.address.street}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        address: { ...prev.address, street: e.target.value },
                      }))
                    }
                    placeholder="Street address"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">City</label>
                  <Input
                    name="address.city"
                    value={formData.address.city}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        address: { ...prev.address, city: e.target.value },
                      }))
                    }
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">State</label>
                  <Select
                    name="address.state"
                    value={formData.address.state}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        address: { ...prev.address, state: value },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent>
                      {[
                        "Andhra Pradesh",
                        "Arunachal Pradesh",
                        "Assam",
                        "Bihar",
                        "Chhattisgarh",
                        "Goa",
                        "Gujarat",
                        "Haryana",
                        "Himachal Pradesh",
                        "Jharkhand",
                        "Karnataka",
                        "Kerala",
                        "Madhya Pradesh",
                        "Maharashtra",
                        "Manipur",
                        "Meghalaya",
                        "Mizoram",
                        "Nagaland",
                        "Odisha",
                        "Punjab",
                        "Rajasthan",
                        "Sikkim",
                        "Tamil Nadu",
                        "Telangana",
                        "Tripura",
                        "Uttar Pradesh",
                        "Uttarakhand",
                        "West Bengal",
                      ].map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Pincode</label>
                  <Input
                    name="address.pincode"
                    value={formData.address.pincode}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        address: { ...prev.address, pincode: e.target.value },
                      }))
                    }
                    placeholder="Pincode"
                    maxLength={6}
                    pattern="[0-9]*"
                  />
                  {fieldErrors.pincode && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.pincode}</p>
                  )}
                </div>
              </div>
            </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t px-4 py-4 sm:px-6">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" className="w-full sm:w-auto">Save Changes</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditProfileModal;
